// The dashboard project selection as a hook (architecture sprint-15 §9.3). Candidates are the caller's active
// projects from GET /api/projects (shift projects included; sprint 13 marks them with `shift`). Activity comes
// from the open shift (['shifts','current']) and the timer (['timer']).
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useMe } from '@/hooks/use-me';
import { useProjects } from '@/hooks/use-projects';
import { useActiveShift } from '@/hooks/use-shifts';
import { useTimer } from '@/hooks/use-timer';
import {
  readStoredProject,
  resolveSelection,
  storeSelectedProject,
  subscribeSelection,
  type ResolvedSelection,
  type SelectionActivity,
  type SelectionCandidate,
} from '@/lib/project-selection';
import { isShiftProject } from '@/types/projects';

export interface SelectedProjectState {
  selection: ResolvedSelection | null;
  candidates: SelectionCandidate[];
  /** false until the projects list has loaded (nothing is decided before). */
  ready: boolean;
  select: (projectId: string) => void;
}

function useActivity(): SelectionActivity | null {
  const shift = useActiveShift()?.shift ?? null;
  const timer = useTimer().data?.timer ?? null;
  if (shift) return { kind: 'shift', project: { id: shift.project.id, name: shift.project.name, color: shift.project.color } };
  if (timer) return { kind: 'standard', project: timer.project ? { id: timer.project.id, name: timer.project.name, color: timer.project.color } : null };
  return null;
}

export function useSelectedProject(): SelectedProjectState {
  const { data: me } = useMe();
  const projects = useProjects();
  const activity = useActivity();
  const userId = me?.id ?? '';
  const storedId = useSyncExternalStore(subscribeSelection, () => (userId ? readStoredProject(userId) : null));
  const candidates = useMemo<SelectionCandidate[]>(
    () =>
      (projects.data ?? [])
        .filter((p) => p.status === 'active')
        .map((p) => ({ id: p.id, name: p.name, color: p.color, kind: isShiftProject(p) ? 'shift' : 'standard' })),
    [projects.data],
  );
  const selection = projects.data
    ? resolveSelection({ candidates, activity, defaultProjectId: me?.preferences.defaultProjectId ?? null, storedId })
    : activity
      ? { project: activity.project, kind: activity.kind, locked: true }
      : null;
  const select = useCallback((projectId: string) => {
    if (userId) storeSelectedProject(userId, projectId);
  }, [userId]);
  return { selection, candidates, ready: projects.data !== undefined, select };
}

/** Mounted once (GlobalShortcuts): remembers the project of a timer or shift the user starts anywhere. */
export function useRememberActivityProject(): void {
  const { data: me } = useMe();
  const activity = useActivity();
  const projectId = activity?.project?.id ?? null;
  const userId = me?.id ?? null;
  useEffect(() => {
    if (userId && projectId) storeSelectedProject(userId, projectId);
  }, [userId, projectId]);
}
